import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  FormDialogMode,
  UserPermission
} from '@globaleris/ui.library'
import { makeStyles, useTheme } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { styles } from './priceCoefficientsTable.styles'

import {
  FormDialogDefinitionProps,
  HttpErrorProps,
  useFormDialog,
  usePageState
} from '../../../hooks'
import { ActionIcons, GlobalNotification } from '../../../model'

import { getCustomerDomain, storeDataTableRowsPerPageForCurrentPage } from '../../../redux'
import { DataTableErrorHandler } from '../../errors/scopes/dataTable/dataTableError'

import { enumToSelectOptionArray } from '../../../helpers'

import { Delete, Edit } from '@material-ui/icons'

import moment from 'moment'
import { PriceCoefficientTable } from '../../../api/Argentia/endpoints/priceCoefficients/model/PriceCoefficient'
import { EnumPeriodoLiquidacion } from '../../../api/oData/enums/enumPeriodoLiquidacion'
import { associateActionIcon } from '../../../helpers/dataTableActionIconHelper'
import { consolidateNullValues } from './priceCoefficientsTable.mapper'

export interface PriceCoefficientsTableProps {
  priceCoefficients?: PriceCoefficientTable[] | null
  round?: number
  errorResponse?: HttpErrorProps | null
  onNotificationChange: (notificacion: GlobalNotification | undefined) => void
  onRefreshPriceCoefficients?: () => void
  // onAddRow: (coefficient: PriceCoefficientTable) => void
  onEditRowValues: (coefficient: PriceCoefficientTable) => void
  onDeleteRow: (id: number | string) => void
  cardMode?: boolean
  noTitle?: boolean
  editMode?: boolean
}

export const PriceCoefficientsTable: React.FC<PriceCoefficientsTableProps> = ({
  priceCoefficients,
  round = 6,
  onNotificationChange,
  onRefreshPriceCoefficients,
  // onAddRow,
  onEditRowValues,
  onDeleteRow,
  editMode = false,
  noTitle = false,
  errorResponse = null,
  cardMode = false
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'priceCoefficient', 'enums'])

  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()
  const { dataTableRowsPerPage } = usePageState()

  const [selectedRow, setSelectedRow] = useState<PriceCoefficientTable>({} as PriceCoefficientTable)

  const coefficientsIcons = (coeff: PriceCoefficientTable[] | null | undefined): any => {
    if (coeff) {
      return coeff.map(p => {
        const actionIcon = p.tempId
          ? associateActionIcon(p, ActionIcons.NEW_ITEM, 'No Guardado')
          : undefined
        return { ...p, actions: actionIcon }
      })
    }
    return coeff
  }

  const coefficients = coefficientsIcons(priceCoefficients)

  const onEditValuesActionHandler = async (coefficient: PriceCoefficientTable) => {
    const plainValues = coefficient?.coefficientValues?.sort(c => c.idPeriod).map(c => c.value)
    openForm({
      definition: editValuesDefinition,
      title: t('priceCoefficient:forms.editValues.title'),
      okBtnLabel: t('priceCoefficient:forms.editValues.actions.okButton'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      inputData: {
        ...plainValues,
        ...coefficient
      },
      onClose: formData => {
        formData ? editPriceValue(formData) : closeForm()
      }
    })
    setSelectedRow({ ...plainValues, ...coefficient })
  }

  const editPriceValue = async (coefficient: PriceCoefficientTable) => {
    onEditRowValues(consolidateNullValues(coefficient))
    setSelectedRow({} as PriceCoefficientTable)
    closeForm()
  }

  const onDeleteActionHandler = async (id: number | string) => {
    onDeleteRow(id)
  }

  const editValuesDefinition: FormDialogDefinitionProps =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT_PRICE_VALUES',
        sections: [
          {
            inputs: [
              {
                name: 'periodDescription',
                label: t('priceCoefficient:dataTable.columns.period'),
                type: DataType.TEXT,
                onChange(value, formState) {
                  setSelectedRow(prevState => ({
                    ...prevState,
                    periodDescription: value.toString()
                  }))
                }
              },
              {
                name: 'idSettlementPeriod',
                label: 'Período de Liquidación',
                type: DataType.SELECT,
                readOnly: selectedRow.id ? true : false,
                selectOptions: enumToSelectOptionArray(
                  EnumPeriodoLiquidacion,
                  'enums:EnumPeriodoLiquidacion'
                ).filter(option => option.value !== EnumPeriodoLiquidacion.Horario),
                onChange(value, formState) {
                  setSelectedRow(prevState => ({
                    ...prevState,
                    idSettlementPeriod: +value
                  }))
                }
              },
              {
                name: 'startDate',
                label: 'Fecha de inicio',
                type: DataType.DATE,
                required: true,
                readOnly: selectedRow.indefinite || selectedRow.id ? true : false,
                onChange(value, formState) {
                  if (moment(value).isValid()) {
                    const date = moment(value).startOf('month').toDate()
                    setSelectedRow(prevState => ({
                      ...prevState,
                      startDate: date
                    }))
                  }
                }
              },
              {
                name: 'endDate',
                label: 'Fecha de fin',
                type: DataType.DATE,
                required: false,
                readOnly: selectedRow.indefinite || selectedRow.id ? true : false,
                onChange(value, formState) {
                  if (moment(value).isValid()) {
                    const date = moment(value).endOf('month').toDate()
                    setSelectedRow(prevState => ({
                      ...prevState,
                      endDate: date
                    }))
                  }
                }
              },
              {
                name: 'indefinite',
                label: t('priceComponent:forms.editValues.inputs.indefinite'),
                type: DataType.SWITCH,
                clearable: true,
                display: !selectedRow.id && !selectedRow.tempId,
                onChange(value, formState) {
                  setSelectedRow(prevState => ({
                    ...prevState,
                    indefinite: Boolean(value),
                    startDate: Boolean(value)
                      ? moment(new Date()).startOf('year').toDate()
                      : prevState.startDate,
                    endDate: undefined
                  }))
                }
              }
            ]
          },
          {
            inputs: [
              {
                name: 'p1',
                label: t('priceCoefficient:dataTable.columns.p1'),
                customDecimalScale: round,
                type: DataType.NUMBER,
                clearable: true
              },
              {
                name: 'p2',
                label: t('priceCoefficient:dataTable.columns.p2'),
                customDecimalScale: round,
                type: DataType.NUMBER,
                clearable: true
              },
              {
                name: 'p3',
                label: t('priceCoefficient:dataTable.columns.p3'),
                customDecimalScale: round,
                type: DataType.NUMBER,
                clearable: true
              },
              {
                name: 'p4',
                label: t('priceCoefficient:dataTable.columns.p4'),
                customDecimalScale: round,
                type: DataType.NUMBER,
                clearable: true
              },
              {
                name: 'p5',
                label: t('priceCoefficient:dataTable.columns.p5'),
                customDecimalScale: round,
                type: DataType.NUMBER,
                clearable: true
              },
              {
                name: 'p6',
                label: t('priceCoefficient:dataTable.columns.p6'),
                customDecimalScale: round,
                type: DataType.NUMBER,
                clearable: true
              }
            ]
          }
        ]
      }
    }, [selectedRow, setSelectedRow])

  const dataTableColumns: DataTableColumn[] = [
    {
      name: 'actions',
      label: '',
      hideForResponsive: true,
      type: DataType.HTML,
      columnWidth: 65
    },
    {
      name: 'id',
      display: false
    },
    {
      name: 'periodDescription',
      label: t('priceCoefficient:dataTable.columns.period'),
      type: DataType.TEXT
    },
    {
      name: 'idSettlementPeriod',
      label: t('priceCoefficient:dataTable.columns.settlement'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumPeriodoLiquidacion, 'enums:EnumPeriodoLiquidacion')
    },

    {
      name: 'startDate',
      label: t('priceCoefficient:dataTable.columns.startDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('priceCoefficient:dataTable.columns.endDate'),
      type: DataType.DATE
    },
    {
      name: 'p1',
      label: t('priceCoefficient:dataTable.columns.p1'),
      type: DataType.NUMBER,
      customDecimalScale: round
    },
    {
      name: 'p2',
      label: t('priceCoefficient:dataTable.columns.p2'),
      type: DataType.NUMBER,
      customDecimalScale: round
    },
    {
      name: 'p3',
      label: t('priceCoefficient:dataTable.columns.p3'),
      type: DataType.NUMBER,
      customDecimalScale: round
    },
    {
      name: 'p4',
      label: t('priceCoefficient:dataTable.columns.p4'),
      type: DataType.NUMBER,
      customDecimalScale: round
    },
    {
      name: 'p5',
      label: t('priceCoefficient:dataTable.columns.p5'),
      type: DataType.NUMBER,
      customDecimalScale: round
    },
    {
      name: 'p6',
      label: t('priceCoefficient:dataTable.columns.p6'),
      type: DataType.NUMBER,
      customDecimalScale: round
    }
  ]

  const dataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: !cardMode,
    elevation: 0,
    filterType: !cardMode ? FilterType.ADVANCED : undefined,
    download: !cardMode,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    selectableRows: false,
    customInsertEditModal: true,
    rowsPerPage: dataTableRowsPerPage || 10,
    rowsPerPageOptions: [5, 10, 20],
    analytics: {
      customer: getCustomerDomain(),
      view: t('priceCoefficient:dataTable.analytics')
    },
    // onRowInsert: onAddValuesActionHandler,
    rowCustomActions: [
      {
        userPermission: UserPermission.EDIT,
        icon: <Edit fontSize="small" />,
        tooltip: t('priceCoefficient:dataTable.customActions.editValue.tooltip'),
        onClick: (row: PriceCoefficientTable) => {
          onEditValuesActionHandler(row)
        }
      },
      {
        userPermission: UserPermission.EDIT,
        icon: <Delete fontSize="small" />,
        tooltip: t('priceCoefficient:dataTable.customActions.deletePeriod.tooltip'),
        onClick: (row: PriceCoefficientTable) => onDeleteActionHandler(row.id ?? row.tempId)
      }
    ],
    onRowsPerPageChange: (rowsPerPage: number) => {
      storeDataTableRowsPerPageForCurrentPage(rowsPerPage)
    }
  }

  useEffect(() => {
    updateFormDefinition(editValuesDefinition, selectedRow)
  }, [editValuesDefinition])

  return (
    <>
      <DataTable
        // title={t('priceCoefficient:title')}
        subtitle={!noTitle ? t('priceCoefficient:title') : undefined}
        data={coefficients}
        columns={dataTableColumns}
        options={dataTableOptions}
        info={DataTableErrorHandler(errorResponse)}
      />
      <CustomFormDialog {...formProps} />
    </>
  )
}
